import { TextStyle } from '@tiptap/extension-text-style';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    fontSize: {
      /** Apply an inline font-size (e.g. "18px") to the current selection / stored marks. */
      setFontSize: (fontSize: string) => ReturnType;
      /** Remove the inline font-size from the current selection / stored marks. */
      unsetFontSize: () => ReturnType;
    };
  }
}

/**
 * FontSize for the shared email composer.
 *
 * Extends tiptap's TextStyle mark with a `fontSize` attribute that renders
 * as an inline `style="font-size: Npx"` — the email-client-safe way to size
 * text (font-size elements / classes are stripped by many clients).
 *
 * Coexists with @tiptap/extension-color (both write to the same textStyle
 * mark; tiptap merges the style attributes). Do NOT also register the plain
 * TextStyle extension alongside this one — it would be a duplicate mark.
 */
export const FontSize = TextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      fontSize: {
        default: null,
        parseHTML: (element) => {
          const raw = (element as HTMLElement).style?.fontSize || '';
          return raw ? raw.replace(/['"]+/g, '') : null;
        },
        renderHTML: (attributes) => {
          if (!attributes.fontSize) return {};
          return { style: `font-size: ${attributes.fontSize}` };
        },
      },
    };
  },

  addCommands() {
    return {
      ...this.parent?.(),
      setFontSize:
        (fontSize: string) =>
        ({ chain }) => {
          return chain().setMark('textStyle', { fontSize }).run();
        },
      unsetFontSize:
        () =>
        ({ chain }) => {
          return chain()
            .setMark('textStyle', { fontSize: null })
            .removeEmptyTextStyle()
            .run();
        },
    };
  },
});
